<script setup lang="ts">
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits, type CheckboxRootEmits } from 'radix-vue'
import { PhCheck, PhMinus } from '@phosphor-icons/vue'
import { cva, type VariantProps } from 'class-variance-authority'
import { computed, type HTMLAttributes } from 'vue'

import { classMerge } from '../utils'

const checkboxVariants = cva('peer bg-background border border-aside-background-stroke w-4 h-4 my-0.5 flex items-center justify-center focus-visible:ring focus-visible:ring-ring focus-visible:ring-offset-1 outline-none data-[state=checked]:border-action-hover disabled:opacity-50 aria-[invalid=true]:border-negative-foreground', {
  variants: {
    size: {
      sm: 'w-4 h-4',
      md: 'w-5 h-5'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

const iconVariants = cva('', {
  variants: {
    size: {
      sm: 'w-3 h-3',
      md: 'w-4 h-4'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

type CheckboxProps = VariantProps<typeof checkboxVariants>

const props = withDefaults(defineProps<{
  indeterminate?: boolean
  size?: CheckboxProps['size']
  disabled?: boolean
  // eslint-disable-next-line vue/no-reserved-props
  class?: HTMLAttributes['class']
}>(), {
  indeterminate: false,
  size: 'md',
  disabled: undefined,
  class: undefined
})

const emits = defineEmits<CheckboxRootEmits>()

const delegatedProps = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)

</script>

<template>
  <CheckboxRoot v-bind="forwarded"
                :class="classMerge(checkboxVariants({ size }), props.class)">
    <CheckboxIndicator class="bg-action-hover text-action-foreground w-full h-full flex items-center justify-center">
      <PhMinus v-if="indeterminate"
               :class="classMerge(iconVariants({ size }), 'w-3 h-3')"
               weight="bold" />
      <PhCheck v-else
               :class="classMerge(iconVariants({ size }), 'w-3 h-3')"
               weight="bold" />
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
